import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  CreateOrUpdateSalesListingRequest,
  PmBuildingType,
  SalesClient,
  SalesListingDto,
} from '@core/api';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { LocationFormlyFieldService } from '@core/services/formly-service/location-formly-field.service';
import { MtxGridColumn, MtxGridModule } from '@ng-matero/extensions/grid';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OverrideModule } from '@override/override.module';
import { MaterialModule } from '@shared/material/material.module';
import moment from 'moment';
import { finalize } from 'rxjs';
import { WebsiteEditModule } from '../website-edit/website-edit.module';
import { SalesListingEditComponent } from './sales-listing-edit';

interface ExtendedSalesListingDto extends SalesListingDto {
  addressForSorting: string;
  cityForSorting: string;
  fullAddress: string;
  titleForSorting: string;
  descriptionForSorting: string;
  specificPriceForSorting: string;
  areaSpecificationForSorting: string;
  buildingTypesForSorting: string;
  isPublishedForSorting: string;
  linksForSorting: string;
}

@Component({
  selector: 'app-website-edit-sale',
  imports: [
    CommonModule,
    OverrideModule,
    MtxGridModule,
    TranslateModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    SmartusComponentsModule,
    WebsiteEditModule,
  ],
  templateUrl: './website-edit-sale.component.html',
  styleUrl: './website-edit-sale.component.scss',
})
export class WebsiteEditSaleComponent implements OnInit, AfterViewInit {
  form = new FormGroup({});
  formFields: Array<FormlyFieldConfig>;
  isLoading = false;
  formModel = {};
  searchform = new FormGroup({
    searchTerm: new FormControl(''),
  });

  public saleListingObjects: ExtendedSalesListingDto[];
  public createOrUpdateSaleListingRequest: CreateOrUpdateSalesListingRequest;
  public filteredSaleListingObjects: ExtendedSalesListingDto[];
  searchTerm = '';

  columnsSaleListings: MtxGridColumn[] = [];

  headline = this.translateService.instant('website_edit.sale.headline');
  isLoadingObjects = false;

  buildingTypes: PmBuildingType[];

  constructor(
    private readonly translateService: TranslateService,
    private readonly fieldService: LocationFormlyFieldService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly salesClient: SalesClient,
    private readonly dialog: MatDialog
  ) {
    this.breadcrumbService.setBreadcrumbs([{ label: this.headline }]);
  }

  ngOnInit(): void {
    this.isLoadingObjects = true;

    this.columnsSaleListings = [
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__archobjectId'
        ),
        field: 'archObjId',
        sortable: true,
        width: '5%',
        minWidth: 100,
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__address'
        ),
        field: 'addressForSorting',
        sortable: true,
        formatter: (data: any) => data.fullAddress || '',
        width: '15%',
        minWidth: 200,
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__title'
        ),
        field: 'titleForSorting',
        sortable: true,
        formatter: data => WebsiteEditSaleComponent.shortenText(data.title, 50),
        width: '15%',
        minWidth: 200,
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__price'
        ),
        field: 'specificPriceForSorting',
        sortable: true,
        formatter: data => {
          const shortenedPrice = WebsiteEditSaleComponent.shortenText(
            data.specificPrice,
            50
          );
          return shortenedPrice && shortenedPrice.length > 0
            ? shortenedPrice
            : this.translateService.instant(
                'rem.properties.label__price_on_request'
              );
        },
        width: '15%',
        minWidth: 200,
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__buildingType'
        ),
        field: 'buildingTypesForSorting',
        sortable: true,
        formatter: data => {
          return data.buildingTypes
            .sort((a: any, b: any) =>
              a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            )
            .map((bt: any) => bt.name)
            .join(', ');
        },
        width: '15%',
        minWidth: 200,
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__area'
        ),
        field: 'areaSpecificationForSorting',
        sortable: true,
        formatter: data =>
          WebsiteEditSaleComponent.shortenText(data.areaSpecification, 50) ??
          '--',
        width: '15%',
        minWidth: 200,
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__description'
        ),
        field: 'descriptionForSorting',
        sortable: true,
        width: '30%',
        minWidth: 300,
        formatter: data =>
          WebsiteEditSaleComponent.shortenText(data.description, 50),
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__link'
        ),
        field: 'linksForSorting',
        sortable: true,
        width: '10%',
        minWidth: 150,
        formatter: data => {
          if (!data.links || data.links.length === 0) {
            return '';
          }

          const formattedLinks = data.links.map(link => {
            const shortenedLink = WebsiteEditSaleComponent.shortenText(
              link.link,
              30
            );

            return link.linkDisplayName
              ? `<small class="text-muted">${link.linkDisplayName}</small><br>(${shortenedLink})`
              : shortenedLink;
          });

          return formattedLinks.join('<br><br>');
        },
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__isPublished'
        ),
        field: 'isPublishedForSorting',
        type: 'boolean',
        sortable: true,
        width: '5%',
        minWidth: 100,
        formatter: data =>
          data.isPublished
            ? this.translateService.instant(
                'website_edit.table_header.label__true'
              )
            : this.translateService.instant(
                'website_edit.table_header.label__false'
              ),
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__createdAt'
        ),
        field: 'createdAt',
        sortable: true,
        width: '10%',
        minWidth: 150,
        formatter: data =>
          data.createdAt
            ? moment
                .utc(data.createdAt)
                .tz('Europe/Berlin')
                .locale(this.translateService.instant('locale.locale'))
                .format(
                  this.translateService.instant(
                    'locale.moment__date_time_format'
                  )
                ) +
              ' ' +
              this.translateService.instant('website_edit.extended_time_str')
            : 'Nicht verfügbar',
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__changedAt'
        ),
        field: 'changeDate',
        sortable: true,
        width: '10%',
        minWidth: 150,
        formatter: data =>
          data.changeDate
            ? moment
                .utc(data.changeDate)
                .tz('Europe/Berlin')
                .locale(this.translateService.instant('locale.locale'))
                .format(
                  this.translateService.instant(
                    'locale.moment__date_time_format'
                  )
                ) +
              ' ' +
              this.translateService.instant('website_edit.extended_time_str')
            : 'Nicht verfügbar',
      },
    ];

    this.getSaleListingObjects();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const headers = document.querySelectorAll('.mtx-grid .mat-sort-header');
      headers.forEach(header => {
        (header as HTMLElement).setAttribute('tabindex', '0');
      });
    });
  }

  /**
   * Applies the search filter to the sale marketing objects.
   */
  applySearch() {
    this.searchTerm = this.searchform.get('searchTerm')?.value ?? '';
    if (this.searchTerm.trim().length > 2) {
      const searchTermLower = this.searchTerm.toLowerCase();

      this.filteredSaleListingObjects = this.saleListingObjects.filter(
        saleListingObject =>
          saleListingObject.address?.city
            ?.toLowerCase()
            .includes(searchTermLower) ||
          saleListingObject.address?.postalCode
            ?.toLowerCase()
            .includes(searchTermLower) ||
          saleListingObject.address?.streetName
            ?.toLowerCase()
            .includes(searchTermLower) ||
          saleListingObject.address?.houseNumber
            ?.toLowerCase()
            .includes(searchTermLower) ||
          saleListingObject.title?.toLowerCase().includes(searchTermLower) ||
          saleListingObject.description
            ?.toLowerCase()
            .includes(searchTermLower) ||
          saleListingObject.archObjId
            ?.toLowerCase()
            .includes(searchTermLower) ||
          saleListingObject.addressForSorting
            ?.toLowerCase()
            .includes(searchTermLower) ||
          saleListingObject.fullAddress
            ?.toLowerCase()
            .includes(searchTermLower) ||
          saleListingObject.links?.some(
            item =>
              item.link?.toLowerCase().includes(searchTermLower) ||
              item.linkDisplayName?.toLowerCase().includes(searchTermLower)
          )
      );
    } else {
      this.filteredSaleListingObjects = [...this.saleListingObjects];
    }
  }

  /**
   * Handles errors by logging them and setting the loading state to false.
   * @param error The error to handle.
   */
  private handleError(error: Error) {
    this.isLoading = false;
    console.error('[WebsiteEditSaleComponent] handleError:', error);
  }

  /**
   * Retrieves the sale marketing objects and updates the filtered list.
   */
  public getSaleListingObjects() {
    this.salesClient.getAllSalesListingObjectsForEdit().subscribe({
      next: (saleMarketingObjects: SalesListingDto[]) => {
        const processedObjects = saleMarketingObjects.map(obj => {
          return {
            ...obj,
            addressForSorting: obj.address
              ? `${obj.address.streetName || ''} ${obj.address.houseNumber || ''}`.trim()
              : '',
            cityForSorting: obj.address?.city || '',
            fullAddress: obj.address
              ? (() => {
                  const street =
                    `${obj.address.streetName || ''} ${obj.address.houseNumber || ''}`.trim();
                  const location =
                    `${obj.address.postalCode || ''} ${obj.address.city || ''}`.trim();
                  return location ? `${street}, ${location}` : street;
                })()
              : '',
            titleForSorting: (obj.title || '').toLowerCase(),
            descriptionForSorting: (obj.description || '').toLowerCase(),
            specificPriceForSorting: this.normalizeForSorting(
              obj.specificPrice || '',
              this.translateService.instant(
                'rem.properties.label__price_on_request'
              )
            ),
            areaSpecificationForSorting: this.normalizeForSorting(
              obj.areaSpecification || ''
            ),
            buildingTypesForSorting:
              (obj.buildingTypes?.length ?? 0) > 0
                ? obj.buildingTypes
                    .map(bt => bt.name.toLowerCase())
                    .sort()
                    .join('|')
                : '',
            isPublishedForSorting: obj.isPublished ? 'Ja' : 'Nein',
            linksForSorting: obj.links?.length
              ? obj.links
                  .map(link =>
                    (link.linkDisplayName || link.link || '').toLowerCase()
                  )
                  .join('|')
              : '',
          };
        });

        this.saleListingObjects = processedObjects.toSorted((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        });
        this.filteredSaleListingObjects = [...this.saleListingObjects];
        this.fieldService.getDefaultList();
      },
      error: (error: Error) => {
        this.handleError(error);
        this.isLoadingObjects = false;
      },
      complete: () => (this.isLoadingObjects = false),
    });
  }

  /**
   * Normalisiert einen String für die Sortierung (case-insensitive, ohne Interpunktion).
   * Extrahiert numerische Werte für korrekte numerische Sortierung.
   * @param text Der zu normalisierende Text.
   * @returns Der normalisierte Text für die Sortierung.
   */
  private normalizeForSorting(text: string, defaultText?: string): string {
    if (!text && !defaultText) {
      return '0000000000000|';
    }

    if (!text && defaultText) {
      return '9999999999999|' + defaultText.toLowerCase();
    }
    // Extrahiere numerische Werte
    const numericMatch = text.match(/[\d.,]+/);
    let numericPart = '9999999999999';
    let textPart = text.toLowerCase().trim();

    if (numericMatch) {
      // Entferne Tausendertrennerpunkte und ersetze Komma durch Punkt für Dezimalzahlen
      const numericValue = numericMatch[0].replace(/\./g, '').replace(',', '.');

      const parsedNumber = parseFloat(numericValue);
      if (!isNaN(parsedNumber)) {
        // Padding mit Nullen für einheitliche String-Länge (bis zu 10 Stellen vor dem Komma)
        numericPart = parsedNumber.toFixed(2).padStart(13, '0');

        textPart = text.replace(numericMatch[0], '').toLowerCase().trim();
      }
    }

    return `${numericPart}|${textPart}`;
  }

  /**
   * Handles the selection of a sale listing by ID.
   * @param id The ID of the sale listing.
   */
  onSelectSaleListing(id: number): void {
    this.isLoading = true;

    this.salesClient.getSalesListingObjectForEdit(id).subscribe({
      next: (saleListingObject: SalesListingDto) => {
        this.openSaleListingEditDialog('edit', saleListingObject);
        this.isLoading = false;
      },
      error: (error: Error) => {
        this.handleError(error);
        this.isLoading = false;
      },
    });
  }

  /**
   * Opens the sale listing edit dialog.
   * @param salesListingObject The sale listing object to edit.
   */
  openSaleListingEditDialog(
    mode: 'add' | 'edit',
    salesListingObject?: SalesListingDto
  ): void {
    this.salesClient
      .getBuildingTypes()
      .pipe(
        finalize(() => {
          const dialogRef = this.dialog.open(SalesListingEditComponent, {
            panelClass: 'smartportal-dialog',
            maxHeight: '800px',
            disableClose: true,
            data: {
              title:
                mode === 'add'
                  ? this.translateService.instant('website_edit.label__create')
                  : this.translateService.instant('website_edit.label__edit'),
              salesListingObject,
              buildingTypes: this.buildingTypes ?? null,
            },
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result?.cancelled) {
              console.log('Dialog wurde abgebrochen');
            } else {
              this.getSaleListingObjects();
            }
          });
        })
      )
      .subscribe({
        next: types => {
          this.buildingTypes = types;
        },
      });
  }

  /**
   * Shortens the given text to the specified maximum length.
   * @param text The text to shorten.
   * @param maxLength The maximum length of the shortened text.
   * @returns The shortened text with ellipsis if it exceeds the maximum length.
   */
  private static shortenText(text: string, maxLength: number): string {
    if (!text || text.length <= maxLength) {
      return text;
    }

    let shortened = text.substring(0, maxLength);

    const lastSpaceIndex = shortened.lastIndexOf(' ');
    if (lastSpaceIndex > 0) {
      shortened = shortened.substring(0, lastSpaceIndex);
    }

    return shortened + '...';
  }
}
